import {
	Button,
	Checkbox,
	CheckboxGroup,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	VStack,
	Text,
	Box,
	Tag,
	TagCloseButton,
	TagLabel,
	Textarea,
	UnorderedList,
	ListItem,
	Select,
	SimpleGrid,
	Divider,
	Wrap,
	WrapItem
} from '@chakra-ui/react';

import { lang } from '@lang';
import { BlueprintDisplayValues, HubSpotBlueprintFormState, HUBSPOT_COUNTERTOP_OPTIONS, HUBSPOT_ISLAND_FINISH_OPTIONS, PassoffChecklistState } from '@features/crm/use-passoff-feature';
import type { JSX } from 'react';
import { Note } from '@rta/rta-blueprint-library';

export type PassoffModalProps = {
	isOpen: boolean;
	onClose: () => void;
	checklist: PassoffChecklistState;
	projectFolder: string;
	cutoutOptions: string;
	additionalDetails: string;
	isSubmitting: boolean;
	isSubmitDisabled: boolean;
	allChecklistItemsChecked: boolean;
	willSubmitQuote: boolean;
	blueprintValues: BlueprintDisplayValues;
	comments: Note[];
	updateChecklist: (key: 'fuelSource' | 'utilityCutouts', value: boolean) => void;
	updateChecklistArray: (key: 'islandDimensions' | 'countertopDimensions', index: number, value: boolean) => void;
	updateProjectFolder: (projectFolder: string) => void;
	updateCutoutOptions: (cutoutOptions: string) => void;
	updateAdditionalDetails: (additionalDetails: string) => void;
	submitPassoff: () => void;
	submitQuote: boolean;
	onSubmitQuoteChange: (value: boolean) => void;
	// HubSpot form props
	hubspotForm: HubSpotBlueprintFormState;
	updateHubspotFormField: <K extends keyof HubSpotBlueprintFormState>(field: K, value: HubSpotBlueprintFormState[K]) => void;
};

// Dropdown options for HubSpot fields
const STANDARD_CUSTOM_OPTIONS = ['Standard', 'Custom'];
const ISLAND_QUANTITY_OPTIONS = ['1', '2', '3', '4+'];
const ISLAND_LENGTH_OPTIONS = ['All islands are 8ft or shorter', 'At least one island is longer than 8ft'];
const ISLAND_LOCATION_OPTIONS = ['Free Standing', 'Against Wall', 'N/A'];
const COUNTERTOP_OVERHANG_OPTIONS = ['Standard Overhang', 'Custom Overhang', 'No Overhang - Against Wall'];
const BOOLEAN_OPTIONS = [{ label: 'No', value: 'false' }, { label: 'Yes', value: 'true' }];
// HubSpot only allows these exact values
const FUEL_SOURCE_OPTIONS = ['Natural Gas', 'Propane', 'N/A'];
const CUTOUT_OPTIONS = ['None', 'Standard Utility Panels', 'Umbrella Opening Cutout', 'Electrical Cutout In Panel', 'Pop-Up Countertop Outlet', 'Cutout In Countertop - Other', 'Cutout In Panel - Other'];
const FUTURE_PROJECT_OPTIONS = [{ label: 'Immediate', value: 'Immediate' }, { label: '3-6 months', value: '3_months' }, { label: '6-9 months', value: '6_months' }, { label: '9-12 months', value: '9_months' }];
const INSTALL_METHOD_OPTIONS = ['DIY', 'Installer Network Contractor', 'Outside Contractor', 'Customer Undecided'];
const PAYMENT_TYPE_OPTIONS = ['Normal', 'Split payment', 'Delayed Payment', 'Paid in full', 'No payment (ambassador orders)'];
const DETAILS_TO_CONFIRM_OPTIONS = ['None', 'Final Dimensions', 'Profile', 'Utility Cutout'];
const ISLAND_HEIGHT_OPTIONS = [
	{ label: 'Bar height only', value: 'bar_height' },
	{ label: 'Grill height only', value: 'grill_height' },
	{ label: 'Both', value: 'both' }
];
const BLUEPRINT_DELAYED_OPTIONS = [{ label: 'No', value: '' }, { label: 'Yes', value: 'Yes' }];
const DELAYED_REASON_OPTIONS = [
	'Family emergency or sickness',
	'Home under construction',
	'PDF Only',
	'Pending HOA Approval',
	'Pending permit approval',
	'Space under construction',
	'Traveling',
	'Waiting for funds',
	'Waiting for samples',
	'Waiting on contractor or installer quotes',
	'Designer Requested Hold',
	'Future Project / Delayed Delivery'
];
const ON_HOLD_SOURCE_OPTIONS = ['Creator', 'Liaison', 'Customer'];
// Layout options - values must match HubSpot's exact allowed options
const KITCHEN_LAYOUT_OPTIONS = [
	{ label: 'Linear', value: 'Linear' },
	{ label: 'Side-by-side', value: 'Side-by-side' },
	{ label: 'L-Shaped', value: 'L-Shaped' },
	{ label: 'Galley', value: 'Galley' },
	{ label: 'U-shaped', value: 'U-shaped' },
	{ label: 'Non-Standard Layout', value: 'Non-Standard Layout' }
];

// HubSpot allowed countertop values (must match exactly).
// Slate, Fantasy Brown also need to exist in HubSpot's `countertop` property enum.
const COUNTERTOP_OPTIONS = [
	'To Be Determined',
	'No Countertop',
	'Chiseled Smoky',
	'Chiseled Hickory',
	'Chiseled Cloudy',
	'Clean Edge Smoky',
	'Clean Edge Hickory',
	'Clean Edge Cloudy',
	'Clean Edge Slate',
	'Granite Viscount White',
	'Granite Steel Gray',
	'Granite Venetian Gold',
	'Granite Alpha White',
	'Granite Thunder White',
	'Granite Giallo Ornamental White',
	'Granite Fantasy Brown',
	'MOKS - Asteroid Terrazo',
	'MOKS - Dove Shimmer',
	'MOKS - Galactic Ice',
	'MOKs - Everest',
	'MOKs - Elegant Gray',
	'MOKs - Dove Gray',
	'MOKs - Antartica',
	'MOKs - Neutral Concrete',
	'MOKs - Silver Gray'
];

// HubSpot allowed island finish values (must match exactly)
const ISLAND_FINISH_OPTIONS = [
	'To Be Determined',
	'Stacked Stone Graphite',
	'Stacked Stone Terra',
	'Stacked Stone Chalk',
	'Modern Concrete Bright',
	'Modern Concrete Industrial',
	'Modern Stone / Ash',
	'Modern Stone / Bright',
	'Reclaimed Brick White',
	'Weathered Wood Ash',
	'Weathered Wood Umber',
	'Plank Charcoal',
	'Plank Bright',
	'Slat Bright',
	'Slat Charcoal',
	'Slat Walnut',
	'Cottage X | Bright',
	'Cottage X | Charcoal',
	'Scratch',
	'MOKs - Pure White',
	'MOKS - Slate Gray',
	'MOKs - Pitch Black',
	'MOKs - Navy Marine',
	'MOKs - Nordic Gray',
	'MOKs - Rubbed Bronze',
	'MOKs - Steel Gray'
];


export const PassoffModal = (props: PassoffModalProps): JSX.Element => {
	const {
		isOpen,
		onClose,
		checklist,
		isSubmitting,
		isSubmitDisabled,
		allChecklistItemsChecked,
		willSubmitQuote,
		blueprintValues,
		comments,
		updateChecklist,
		updateChecklistArray,
		submitPassoff,
		submitQuote,
		onSubmitQuoteChange,
		hubspotForm,
		updateHubspotFormField
	} = props;

	// Helper to format island name (strips embedded dimensions after " | " and removes " Island" suffix)
	const formatIslandName = (name: string): string => {
		// First, extract only the part before " | " to remove embedded dimensions
		const baseName = name.split(' | ')[0];
		// Then remove " Island" suffix if present
		return baseName.replace(/ Island$/i, '');
	};

	// Styled label component for black text labels
	const Label = ({ children }: { children: React.ReactNode }) => (
		<Text as="span" color="black" fontWeight="medium">{children}</Text>
	);

	// Styled value component for accent colored measurement values
	const Value = ({ children }: { children: React.ReactNode }) => (
		<Text as="span" color="blue.600" fontWeight="bold">{children}</Text>
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
			<ModalOverlay />
			<ModalContent maxH="90vh">
				<ModalHeader>Submit Blueprint to HubSpot</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing={6} align="stretch">
						{/* HubSpot Blueprint Form Fields */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">Blueprint Details</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{/* Blueprint Name */}
								<FormControl>
									<FormLabel fontSize="sm">Blueprint Name</FormLabel>
									<Input
										size="sm"
										value={hubspotForm.blueprint_name}
										onChange={(e) => updateHubspotFormField('blueprint_name', e.target.value)}
									/>
								</FormControl>

								{/* Standard / Custom */}
								<FormControl>
									<FormLabel fontSize="sm">Standard / Custom</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.standard___custom}
										onChange={(e) => updateHubspotFormField('standard___custom', e.target.value)}
									>
										{STANDARD_CUSTOM_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Contact Name */}
								<FormControl>
									<FormLabel fontSize="sm">Contact Name</FormLabel>
									<Input
										size="sm"
										value={hubspotForm.contact_name}
										onChange={(e) => updateHubspotFormField('contact_name', e.target.value)}
									/>
								</FormControl>

								{/* Contact Email */}
								<FormControl>
									<FormLabel fontSize="sm">Contact Email</FormLabel>
									<Input
										size="sm"
										type="email"
										value={hubspotForm.contact_email}
										onChange={(e) => updateHubspotFormField('contact_email', e.target.value)}
									/>
								</FormControl>
							</SimpleGrid>
						</Box>

						<Divider />

						{/* Island Configuration */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">Island Configuration</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{/* Island Finish */}
								<FormControl>
									<FormLabel fontSize="sm">Island Finish</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.island_finish}
										onChange={(e) => updateHubspotFormField('island_finish', e.target.value)}
									>
										<option value="">Select finish...</option>
										{ISLAND_FINISH_OPTIONS.map((opt) => {
											const invalidFinish = !HUBSPOT_ISLAND_FINISH_OPTIONS.has(opt);
											return (
												<option key={opt} value={opt} disabled={invalidFinish}>
													{invalidFinish ? `${opt} — ⚠ not a valid HubSpot property` : opt}
												</option>
											);
										})}
									</Select>
								</FormControl>

								{/* Island Layout */}
								<FormControl>
									<FormLabel fontSize="sm">Island Layout</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.kitchen_layout}
										onChange={(e) => updateHubspotFormField('kitchen_layout', e.target.value)}
									>
										<option value="">Select layout...</option>
										{KITCHEN_LAYOUT_OPTIONS.map(opt => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Select>
								</FormControl>

								{/* Island Count */}
								<FormControl>
									<FormLabel fontSize="sm">Island Count</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.island_quantity}
										onChange={(e) => updateHubspotFormField('island_quantity', e.target.value)}
									>
										{ISLAND_QUANTITY_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Island Length */}
								<FormControl>
									<FormLabel fontSize="sm">Island Length</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.island_length}
										onChange={(e) => updateHubspotFormField('island_length', e.target.value)}
									>
										{ISLAND_LENGTH_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Island Location */}
								<FormControl>
									<FormLabel fontSize="sm">Island Location</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.island_location}
										onChange={(e) => updateHubspotFormField('island_location', e.target.value)}
									>
										{ISLAND_LOCATION_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Island Height - shown when layout is not Linear */}
								{hubspotForm.kitchen_layout && hubspotForm.kitchen_layout !== 'Linear' && (
									<FormControl>
										<FormLabel fontSize="sm">Select Island Height(s)</FormLabel>
										<Select
											size="sm"
											value={hubspotForm.island_height}
											onChange={(e) => updateHubspotFormField('island_height', e.target.value)}
										>
											<option value="">Select height...</option>
											{ISLAND_HEIGHT_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>{opt.label}</option>
											))}
										</Select>
									</FormControl>
								)}

								{/* Contains Corner Stand - shown when layout is L-Shaped, U-shaped, or Non-Standard Layout */}
								{(hubspotForm.kitchen_layout === 'L-Shaped' || hubspotForm.kitchen_layout === 'U-shaped' || hubspotForm.kitchen_layout === 'Non-Standard Layout') && (
									<FormControl>
										<FormLabel fontSize="sm">Contains Corner Stand</FormLabel>
										<Select
											size="sm"
											value={hubspotForm.contains_corner_stand}
											onChange={(e) => updateHubspotFormField('contains_corner_stand', e.target.value)}
										>
											{BOOLEAN_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>{opt.label}</option>
											))}
										</Select>
									</FormControl>
								)}

								{/* L-shape is offset - shown when layout is L-Shaped */}
								{hubspotForm.kitchen_layout === 'L-Shaped' && (
									<FormControl>
										<FormLabel fontSize="sm">L-shape is offset</FormLabel>
										<Select
											size="sm"
											value={hubspotForm.l_shape_is_offset}
											onChange={(e) => updateHubspotFormField('l_shape_is_offset', e.target.value)}
										>
											{BOOLEAN_OPTIONS.map((opt) => (
												<option key={opt.value} value={opt.value}>{opt.label}</option>
											))}
										</Select>
									</FormControl>
								)}
							</SimpleGrid>
						</Box>

						<Divider />

						{/* Countertop & Materials */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">Countertop & Materials</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{/* Countertop */}
								<FormControl>
									<FormLabel fontSize="sm">Countertop</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.countertop}
										onChange={(e) => updateHubspotFormField('countertop', e.target.value)}
									>
										<option value="">Select countertop...</option>
										{COUNTERTOP_OPTIONS.map((opt) => {
											const invalidCountertop = !HUBSPOT_COUNTERTOP_OPTIONS.has(opt);
											return (
												<option key={opt} value={opt} disabled={invalidCountertop}>
													{invalidCountertop ? `${opt} — ⚠ not a valid HubSpot property` : opt}
												</option>
											);
										})}
									</Select>
								</FormControl>

								{/* Countertop Overhang */}
								<FormControl>
									<FormLabel fontSize="sm">Countertop Overhang</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.countertop_overhang}
										onChange={(e) => updateHubspotFormField('countertop_overhang', e.target.value)}
									>
										{COUNTERTOP_OVERHANG_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Contains Non-Coyote Appliances */}
								<FormControl>
									<FormLabel fontSize="sm">Contains Non-Coyote Appliances</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.contains_non_coyote_appliances}
										onChange={(e) => updateHubspotFormField('contains_non_coyote_appliances', e.target.value)}
									>
										{BOOLEAN_OPTIONS.map(opt => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Select>
								</FormControl>

								{/* Fuel Source */}
								<FormControl>
									<FormLabel fontSize="sm">Fuel Source</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.fuel_source}
										onChange={(e) => updateHubspotFormField('fuel_source', e.target.value)}
									>
										{FUEL_SOURCE_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Cutout Options (multi-select) */}
								<FormControl gridColumn={{ md: 'span 2' }}>
									<FormLabel fontSize="sm">Cutout Options</FormLabel>
									{hubspotForm.cutout_options_2.length > 0 && !hubspotForm.cutout_options_2.includes('None') && (
										<Wrap spacing={1} mb={2}>
											{hubspotForm.cutout_options_2.map((opt) => (
												<WrapItem key={opt}>
													<Tag size="sm" colorScheme="blue" borderRadius="full">
														<TagLabel>{opt}</TagLabel>
														<TagCloseButton onClick={() => {
															const updated = hubspotForm.cutout_options_2.filter((v) => v !== opt);
															updateHubspotFormField('cutout_options_2', updated.length > 0 ? updated : ['None']);
														}} />
													</Tag>
												</WrapItem>
											))}
										</Wrap>
									)}
									<CheckboxGroup
										value={hubspotForm.cutout_options_2}
										onChange={(values) => {
											const selected = values as Array<string>;
											if (selected.includes('None') && !hubspotForm.cutout_options_2.includes('None')) {
												updateHubspotFormField('cutout_options_2', ['None']);
											} else {
												const filtered = selected.filter((v) => v !== 'None');
												updateHubspotFormField('cutout_options_2', filtered.length > 0 ? filtered : ['None']);
											}
										}}
									>
										<SimpleGrid columns={{ base: 1, md: 2 }} spacing={1}>
											{CUTOUT_OPTIONS.map((opt) => (
												<Checkbox key={opt} value={opt} size="sm">
													<Text fontSize="xs">{opt}</Text>
												</Checkbox>
											))}
										</SimpleGrid>
									</CheckboxGroup>
								</FormControl>
							</SimpleGrid>
						</Box>

						<Divider />

						{/* Project & Payment */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">Project & Payment</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{/* Future Project Approval Timeline */}
								<FormControl>
									<FormLabel fontSize="sm">Future Project Approval Timeline</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.future_project_approval_timeline}
										onChange={(e) => updateHubspotFormField('future_project_approval_timeline', e.target.value)}
									>
										{FUTURE_PROJECT_OPTIONS.map(opt => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Select>
								</FormControl>

								{/* Delayed Delivery */}
								<FormControl>
									<FormLabel fontSize="sm">Delayed Delivery</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.delayed_delivery}
										onChange={(e) => updateHubspotFormField('delayed_delivery', e.target.value)}
									>
										{BOOLEAN_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Select>
								</FormControl>

								{/* Projected Delivery Date - shown when Delayed Delivery is true */}
								{hubspotForm.delayed_delivery === 'true' && (
									<FormControl>
										<FormLabel fontSize="sm">Projected Delivery Date</FormLabel>
										<Input
											size="sm"
											type="date"
											value={hubspotForm.projected_delivery_date}
											onChange={(e) => updateHubspotFormField('projected_delivery_date', e.target.value)}
										/>
									</FormControl>
								)}

								{/* Blueprint Delayed */}
								<FormControl>
									<FormLabel fontSize="sm">Blueprint Delayed</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.blueprint_delayed}
										onChange={(e) => updateHubspotFormField('blueprint_delayed', e.target.value)}
									>
										{BLUEPRINT_DELAYED_OPTIONS.map((opt) => (
											<option key={opt.value} value={opt.value}>{opt.label}</option>
										))}
									</Select>
								</FormControl>

								{/* Delayed Reason - shown when Blueprint Delayed is Yes */}
								{hubspotForm.blueprint_delayed === 'Yes' && (
									<FormControl>
										<FormLabel fontSize="sm">Delayed Reason</FormLabel>
										<Select
											size="sm"
											value={hubspotForm.delayed_reason}
											onChange={(e) => updateHubspotFormField('delayed_reason', e.target.value)}
										>
											<option value="">Select reason...</option>
											{DELAYED_REASON_OPTIONS.map((opt) => (
												<option key={opt} value={opt}>{opt}</option>
											))}
										</Select>
									</FormControl>
								)}

								{/* Install Method */}
								<FormControl>
									<FormLabel fontSize="sm">Install Method</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.install_method}
										onChange={(e) => updateHubspotFormField('install_method', e.target.value)}
									>
										{INSTALL_METHOD_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Payment Type */}
								<FormControl>
									<FormLabel fontSize="sm">Payment Type</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.payment_type}
										onChange={(e) => updateHubspotFormField('payment_type', e.target.value)}
									>
										{PAYMENT_TYPE_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Details to Confirm */}
								<FormControl>
									<FormLabel fontSize="sm">Details to Confirm</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.details_to_confirmed}
										onChange={(e) => updateHubspotFormField('details_to_confirmed', e.target.value)}
									>
										{DETAILS_TO_CONFIRM_OPTIONS.map(opt => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Project Folder */}
								<FormControl isRequired>
									<FormLabel fontSize="sm">Project Folder Link</FormLabel>
									<Input
										size="sm"
										value={hubspotForm.project_folder}
										onChange={(e) => updateHubspotFormField('project_folder', e.target.value)}
										placeholder="Paste Google Drive link"
									/>
								</FormControl>
							</SimpleGrid>
						</Box>

						<Divider />

						{/* On Hold Fields - optional section for placing blueprint on hold */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">On Hold (Optional)</Text>
							<Text fontSize="xs" color="gray.500" mb={3}>
								Only fill these fields if the blueprint needs to be placed on hold.
							</Text>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{/* On Hold Source */}
								<FormControl>
									<FormLabel fontSize="sm">On Hold Source</FormLabel>
									<Select
										size="sm"
										value={hubspotForm.on_hold_source}
										onChange={(e) => updateHubspotFormField('on_hold_source', e.target.value)}
									>
										<option value="">None</option>
										{ON_HOLD_SOURCE_OPTIONS.map((opt) => (
											<option key={opt} value={opt}>{opt}</option>
										))}
									</Select>
								</FormControl>

								{/* Projected Off Hold Date - shown when on_hold_source has a value */}
								{hubspotForm.on_hold_source && (
									<FormControl>
										<FormLabel fontSize="sm">Projected Off Hold Date</FormLabel>
										<Input
											size="sm"
											type="date"
											value={hubspotForm.projected_off_hold_date}
											onChange={(e) => updateHubspotFormField('projected_off_hold_date', e.target.value)}
										/>
									</FormControl>
								)}

								{/* On Hold Reason - shown when on_hold_source has a value */}
								{hubspotForm.on_hold_source && (
									<FormControl gridColumn={{ md: 'span 2' }}>
										<FormLabel fontSize="sm">On Hold Reason</FormLabel>
										<Textarea
											size="sm"
											value={hubspotForm.on_hold_reason}
											onChange={(e) => updateHubspotFormField('on_hold_reason', e.target.value)}
											placeholder="Describe reason for placing on hold..."
											rows={2}
										/>
									</FormControl>
								)}
							</SimpleGrid>
						</Box>

						<Divider />

						{/* Notes */}
						<Box>
							<Text fontWeight="bold" mb={3} fontSize="lg">Notes</Text>
							<VStack spacing={4} align="stretch">
								{/* Additional Details */}
								<FormControl>
									<FormLabel fontSize="sm">Additional Details</FormLabel>
									<Textarea
										size="sm"
										value={hubspotForm.additional_details}
										onChange={(e) => updateHubspotFormField('additional_details', e.target.value)}
										placeholder="Any additional details..."
										rows={3}
									/>
								</FormControl>

								{/* PSM Notes */}
								<FormControl>
									<FormLabel fontSize="sm">PSM Notes</FormLabel>
									<Textarea
										size="sm"
										value={hubspotForm.css_notes}
										onChange={(e) => updateHubspotFormField('css_notes', e.target.value)}
										placeholder="Notes for PSM..."
										rows={3}
									/>
								</FormControl>
							</VStack>
						</Box>

						<Divider />

						{/* Verification Checklist */}
						<Box>
							<Text fontWeight="bold" mb={2}>Verification Checklist</Text>
							<Text fontSize="sm" color="gray.600" mb={3}>
								Please verify the following items before submitting:
							</Text>
							<VStack spacing={4} align="stretch" pl={2}>
								{/* Fuel Source */}
								<Box borderLeft="2px solid" borderColor="gray.200" pl={3}>
									<Text fontSize="sm" fontWeight="medium" color="gray.700">
										Fuel Source: <Text as="span" fontWeight="bold" color="blue.600">{blueprintValues?.fuelSource || 'N/A'}</Text>
									</Text>
									<Checkbox
										isChecked={checklist.fuelSource}
										onChange={(e) => updateChecklist('fuelSource', e.target.checked)}
										colorScheme="green"
										mt={1}
									>
										<Text fontSize="sm">I confirm this is correct</Text>
									</Checkbox>
								</Box>

								{/* Island Dimensions */}
								<Box borderLeft="2px solid" borderColor="gray.200" pl={3}>
									<Text fontSize="sm" fontWeight="medium" color="gray.700">
										Island Dimensions:
									</Text>
									{blueprintValues?.islandDimensions?.map((island, idx) => (
										<Box key={idx} ml={2} mb={2}>
											<Text fontSize="sm">
												<Label>{formatIslandName(island.name)}: Length </Label>
												<Value>{island.width}"</Value>
												<Label> | Height </Label>
												<Value>{island.height}"</Value>
												{island.depth > 0 && (
													<>
														<Label> | Depth </Label>
														<Value>{island.depth}"</Value>
													</>
												)}
											</Text>
											<Checkbox
												isChecked={checklist.islandDimensions[idx] ?? false}
												onChange={(e) => updateChecklistArray('islandDimensions', idx, e.target.checked)}
												colorScheme="green"
												size="sm"
											>
												<Text fontSize="xs">I confirm this is correct</Text>
											</Checkbox>
										</Box>
									))}
									{(!blueprintValues?.islandDimensions || blueprintValues.islandDimensions.length === 0) && (
										<Text fontSize="sm" color="gray.500" ml={2}>No islands configured</Text>
									)}
								</Box>

								{/* Countertop Dimensions */}
								<Box borderLeft="2px solid" borderColor="gray.200" pl={3}>
									<Text fontSize="sm" fontWeight="medium" color="gray.700">
										Countertop Dimensions:
									</Text>
									{blueprintValues?.countertopDimensions?.map((countertop, idx) => (
										<Box key={idx} ml={2} mb={2}>
											<Text fontSize="sm">
												<Label>{formatIslandName(countertop.name)}: Length </Label>
												<Value>{countertop.width}"</Value>
												{countertop.depth > 0 && (
													<>
														<Label> | Depth </Label>
														<Value>{countertop.depth}"</Value>
													</>
												)}
											</Text>
											<Checkbox
												isChecked={checklist.countertopDimensions[idx] ?? false}
												onChange={(e) => updateChecklistArray('countertopDimensions', idx, e.target.checked)}
												colorScheme="green"
												size="sm"
											>
												<Text fontSize="xs">I confirm this is correct</Text>
											</Checkbox>
										</Box>
									))}
									{(!blueprintValues?.countertopDimensions || blueprintValues.countertopDimensions.length === 0) && (
										<Text fontSize="sm" color="gray.500" ml={2}>No countertops configured</Text>
									)}
								</Box>

								{/* Utility Cutouts */}
								<Box borderLeft="2px solid" borderColor="gray.200" pl={3}>
									<Text fontSize="sm" fontWeight="medium" color="gray.700">
										Utility Cutouts
									</Text>
									<Text fontSize="xs" color="gray.500" ml={2}>
										Please verify all utility cutouts are properly configured
									</Text>
									<Checkbox
										isChecked={checklist.utilityCutouts}
										onChange={(e) => updateChecklist('utilityCutouts', e.target.checked)}
										colorScheme="green"
										mt={1}
									>
										<Text fontSize="sm">I confirm all utility cutouts are correct</Text>
									</Checkbox>
								</Box>
							</VStack>
						</Box>

						{/* Designer Comments as Bullet List */}
						{comments.length > 0 && (
							<Box>
								<Text fontWeight="bold" mb={2}>Designer Comments</Text>
								<Box
									borderWidth="1px"
									borderColor="gray.200"
									borderRadius="md"
									p={3}
									maxHeight="150px"
									overflowY="auto"
								>
									<UnorderedList spacing={2}>
										{comments.map((comment) => (
											<ListItem key={comment.id} fontSize="sm" color="gray.700">
												{comment.message}
											</ListItem>
										))}
									</UnorderedList>
								</Box>
							</Box>
						)}

						{/* Status messages - shown in priority order */}
						{!hubspotForm.project_folder.trim() && (
							<Text fontSize="sm" color="orange.500">
								{lang.crm.tooltip.passOffMissingProjectFolder}
							</Text>
						)}
						{!allChecklistItemsChecked && (
							<Text fontSize="sm" color="orange.500">
								{lang.crm.tooltip.passOffMissingChecklist}
							</Text>
						)}
						{willSubmitQuote && (
							<Checkbox
								isChecked={submitQuote}
								onChange={(e) => onSubmitQuoteChange(e.target.checked)}
								colorScheme="yellow"
							>
								<Text fontSize="sm" color="yellow.600" fontWeight="medium">
									{lang.crm.tooltip.passOffWillSubmitQuote}
								</Text>
							</Checkbox>
						)}
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose}>
						Cancel
					</Button>
					<Button
						colorScheme="green"
						onClick={submitPassoff}
						isDisabled={isSubmitDisabled}
						isLoading={isSubmitting}
						loadingText="Submitting..."
					>
						Submit to HubSpot
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
